import SendIcon from "@mui/icons-material/Send";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { useEffect, useLayoutEffect, useRef } from "react";
import OthersRankSorter from "../../../components/ResponseComponents/OthersRankSorter";
import { commonWrapper } from "../../../components/ResponseComponents/styles";
import { ActionButton } from "../../../components/ui";
import { inputStyle } from "../../../components/ui/CommonTextInput/style";
import {
  inputContainer,
  placeholderContainer,
} from "../../../components/ui/DefaultInput/styles";
import TransitionLoader from "../../../components/ui/TransitionLoader";
import { useThemeContext } from "../../../globalContext/global-context";
import { hostThemes } from "../../../theme/hostThemes";
import type { CustomMessageContent, ThemeV2 } from "../../../util/types";
import type {
  HostControlBindings,
  QuestionPrompt,
  SecondaryActionBinding,
} from "../../contracts";
import {
  AnswerContent,
  QuestionAnnouncement,
  type QuestionHostFrameProps,
} from "../../QuestionFrameShared";
import { usePendingMessage } from "../shared/usePendingMessage";

const footerStyle = (theme: ThemeV2) => ({
  ...inputContainer(theme),
  boxSizing: "border-box" as const,
  minHeight: hostThemes.chat.composer.height,
  padding: `${theme.tokens.spacing["3"]}px ${theme.tokens.spacing["4"]}px`,
  gap: `${theme.tokens.spacing["2"]}px`,
});

const sendButtonStyle = (theme: ThemeV2) => ({
  width: 40,
  minWidth: 40,
  height: 40,
  padding: 0,
  color: theme.tokens.color.action.primaryText,
});

export const ChatChoiceFooter = ({
  controls,
  hintKey,
}: {
  controls: HostControlBindings;
  hintKey: keyof ThemeV2["copy"]["hints"];
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box data-testid="default-input" id="default-input" sx={footerStyle(theme)}>
      <Box
        data-testid="default-input-placeholder"
        sx={placeholderContainer(theme)}
      >
        {theme.copy.hints[hintKey]}
      </Box>
      <Button
        data-testid="send-btn"
        aria-label={controls.primaryAction?.label ?? theme.copy.actions.send}
        aria-busy={controls.primaryAction?.pending}
        disabled={!controls.primaryAction || controls.primaryAction.disabled}
        onClick={controls.primaryAction?.run}
        sx={sendButtonStyle(theme)}
      >
        <SendIcon />
      </Button>
    </Box>
  );
};

const ChatPromptBubble = ({ prompt }: { prompt: QuestionPrompt }) => {
  const message = {
    type: "text",
    content: prompt.content,
    self: false,
    image: prompt.image,
    alt_text: prompt.altText,
    error_message: prompt.error,
  } as CustomMessageContent;
  return <OthersRankSorter message={message} timerImageOPen={false} />;
};

const ChatPendingBubble = () => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const message = usePendingMessage(theme.copy.status.transitions);
  return (
    <Box
      data-testid="chat-typing-bubble"
      sx={{ ...commonWrapper(theme, false, false), display: "inline-flex" }}
    >
      <TransitionLoader
        id="chat-typing-indicator"
        message={message}
        announcement={
          theme.copy.status.transitions[0] ?? theme.copy.status.loading
        }
      />
    </Box>
  );
};

const ChatSecondaryAction = ({
  action,
  testId = "secondary-answer-action",
}: {
  action?: SecondaryActionBinding;
  testId?: string;
}) => {
  if (!action) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <ActionButton
        testId={testId}
        currentVal={{ text: action.label, display_text: true }}
        isSelected={action.selected}
        ariaPressed={action.selected}
        disabled={action.disabled}
        onClick={action.run}
      />
    </Box>
  );
};

const ChatComposer = ({
  controls,
  input,
  inputHint,
}: {
  controls: HostControlBindings;
  input: NonNullable<HostControlBindings["textInput"]>;
  inputHint?: QuestionHostFrameProps["inputHint"];
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [input.fieldId]);

  return (
    <Box
      data-testid="chat-composer"
      sx={{
        ...footerStyle(theme),
        height: hostThemes.chat.composer.height,
        alignItems: "center",
      }}
    >
      <TextField
        data-testid="text-input-field"
        inputRef={inputRef}
        id={input.fieldId}
        placeholder={input.hint ?? theme.copy.hints[inputHint ?? "others"]}
        value={input.value}
        disabled={input.disabled}
        onChange={(event) => input.onChange(event.target.value)}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            if (!controls.primaryAction?.disabled) input.onConfirm();
          }
        }}
        multiline
        maxRows={10}
        size="small"
        error={Boolean(input.value && input.error)}
        sx={{
          ...inputStyle(theme, false, Boolean(input.prefix || input.suffix)),
          height: 40,
          flex: 1,
          minWidth: 0,
        }}
        slotProps={{
          input: {
            startAdornment: input.prefix ? (
              <InputAdornment position="start">{input.prefix}</InputAdornment>
            ) : undefined,
            endAdornment: input.suffix ? (
              <InputAdornment
                position="end"
                sx={{ whiteSpace: "nowrap", minWidth: "max-content" }}
              >
                {input.suffix}
              </InputAdornment>
            ) : undefined,
          },
          htmlInput: { inputMode: input.inputMode, "aria-label": input.label },
        }}
      />
      <Button
        data-testid="send-btn"
        aria-label={controls.primaryAction?.label ?? theme.copy.actions.send}
        disabled={!controls.primaryAction || controls.primaryAction.disabled}
        onClick={controls.primaryAction?.run}
        sx={sendButtonStyle(theme)}
      >
        <SendIcon />
      </Button>
    </Box>
  );
};

const ChatViewComposer = ({
  response,
  controls,
}: {
  response: React.ReactNode;
  controls: HostControlBindings;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box
      data-testid="chat-composer"
      sx={{
        ...footerStyle(theme),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{response}</Box>
      <Button
        data-testid="send-btn"
        aria-label={controls.primaryAction?.label ?? theme.copy.actions.send}
        disabled={!controls.primaryAction || controls.primaryAction.disabled}
        onClick={controls.primaryAction?.run}
        sx={sendButtonStyle(theme)}
      >
        <SendIcon />
      </Button>
    </Box>
  );
};

export const ChatQuestionHost = (props: QuestionHostFrameProps) => {
  const { attemptId, prompt, promptInHistory, history } = props;
  const { globalObject } = useThemeContext();
  const theme: ThemeV2 = globalObject.themeData.normalizedTheme;
  const chatContentRef = useRef<HTMLDivElement>(null);
  const chatContentInnerRef = useRef<HTMLDivElement>(null);
  const followNewQuestionRef = useRef(true);
  const autoSubmittedAttemptRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      !props.autoSubmit?.ready ||
      autoSubmittedAttemptRef.current === attemptId
    )
      return;
    autoSubmittedAttemptRef.current = attemptId;
    props.autoSubmit.run();
  }, [attemptId, props.autoSubmit]);
  useLayoutEffect(() => {
    const scroll = chatContentRef.current;
    const content = chatContentInnerRef.current;
    if (!scroll || !content) return;
    followNewQuestionRef.current = true;
    const scrollToEnd = () => {
      if (followNewQuestionRef.current) scroll.scrollTop = scroll.scrollHeight;
    };
    scrollToEnd();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(scrollToEnd);
    observer.observe(content);
    observer.observe(scroll);
    return () => observer.disconnect();
  }, [attemptId]);
  const stopFollowingNewQuestion = () => {
    followNewQuestionRef.current = false;
  };
  const hasPrompt = Boolean(prompt.content || prompt.image || prompt.error);
  const chatComposer = props.input === "host-text" && props.controls.textInput;
  const secondaryAction = props.controls.secondaryAction
    ? {
        action: props.controls.secondaryAction,
        testId: "secondary-answer-action",
      }
    : props.input === "none" && props.autoResponse
      ? {
          action: {
            label: theme.copy.actions.dontKnow,
            selected: false,
            disabled: props.autoResponse.disabled,
            run: props.autoResponse.run,
          },
          testId: "auto-response",
        }
      : undefined;
  const answerContent = (
    <AnswerContent
      primaryActionTestId="send-btn"
      controls={{
        ...props.controls,
        secondaryAction: undefined,
        ...(chatComposer
          ? {
              textInput: undefined,
              primaryAction: undefined,
            }
          : {}),
      }}
      primaryActionOwner={props.primaryActionOwner}
      input={props.input}
      hidePrimaryAction={props.input === "none"}
      responseContent={props.response}
    />
  );

  return (
    <Box
      data-testid="question-host-chat-conversation"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "clip",
      }}
    >
      {hasPrompt && <QuestionAnnouncement prompt={prompt} />}
      <Box
        ref={chatContentRef}
        data-testid="chat-content-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        onWheel={stopFollowingNewQuestion}
        onTouchStart={stopFollowingNewQuestion}
        onPointerDown={stopFollowingNewQuestion}
      >
        <Box
          ref={chatContentInnerRef}
          sx={{ flex: "1 0 auto", display: "flex", flexDirection: "column" }}
        >
          {history}
          {!promptInHistory && hasPrompt && (
            <Box
              sx={{
                paddingInline: `${theme.tokens.spacing["4"]}px`,
                paddingTop: `${theme.tokens.spacing["4"]}px`,
              }}
            >
              <ChatPromptBubble prompt={prompt} />
            </Box>
          )}
          {!props.busy &&
            (props.input === "none" || props.input === "host-text") && (
              <Box
                data-testid="chat-response"
                sx={{
                  marginTop: "auto",
                  paddingInline: `${theme.tokens.spacing["4"]}px`,
                  paddingTop: `${theme.tokens.spacing["4"]}px`,
                  paddingBottom: `${theme.tokens.spacing["4"]}px`,
                  color: theme.tokens.color.text.primary,
                }}
              >
                {answerContent}
                <ChatSecondaryAction
                  action={secondaryAction?.action}
                  testId={secondaryAction?.testId}
                />
              </Box>
            )}
          {(props.busy || props.controls.primaryAction?.pending) && (
            <Box
              sx={{
                paddingInline: `${theme.tokens.spacing["4"]}px`,
                paddingBottom: `${theme.tokens.spacing["4"]}px`,
              }}
            >
              <ChatPendingBubble />
            </Box>
          )}
        </Box>
      </Box>
      {!props.busy && chatComposer && (
        <Box sx={{ flexShrink: 0 }}>
          <ChatComposer
            controls={props.controls}
            input={chatComposer}
            inputHint={props.inputHint}
          />
        </Box>
      )}
      {!props.busy && props.input === "custom" && (
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              paddingInline: `${theme.tokens.spacing["4"]}px`,
              paddingBottom: `${theme.tokens.spacing["2"]}px`,
            }}
          >
            <ChatSecondaryAction action={secondaryAction?.action} />
          </Box>
          <ChatViewComposer
            response={props.response}
            controls={props.controls}
          />
        </Box>
      )}
      {(props.busy || props.input === "none") && (
        <Box sx={{ flexShrink: 0 }}>
          <ChatChoiceFooter
            controls={
              props.busy
                ? { ...props.controls, primaryAction: undefined }
                : props.controls
            }
            hintKey={
              props.input === "host-text"
                ? (props.inputHint ?? "others")
                : (props.choiceHint ?? "multichoice")
            }
          />
        </Box>
      )}
    </Box>
  );
};
